import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import {
  Cog6ToothIcon,
  UserPlusIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  BanknotesIcon,
  LockClosedIcon,
  SparklesIcon,
  TicketIcon
} from '@heroicons/react/24/outline';

// Toggle Switch Component
function ToggleSwitch({ enabled, onChange, disabled = false }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-surface-900 ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      } ${enabled ? 'bg-primary-600' : 'bg-surface-600'}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// Setting Card Component
function SettingCard({ title, description, icon: Icon, children, infoText }) {
  return (
    <div className="card">
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-xl bg-primary-500/10">
          <Icon className="w-6 h-6 text-primary-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-surface-100">{title}</h3>
          <p className="text-surface-400 text-sm mt-1">{description}</p>
          
          <div className="mt-4">
            {children}
          </div>
          
          {infoText && (
            <div className="mt-4 flex items-start gap-2 p-3 bg-surface-700/50 rounded-lg">
              <InformationCircleIcon className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-surface-400">{infoText}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SystemSettings() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [savingBeta, setSavingBeta] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [settings, setSettings] = useState({
    registrationEnabled: false,
    bankAccount: {
      bankName: '',
      accountName: '',
      accountNumber: '',
      swiftCode: '',
      branchName: '',
      notes: ''
    }
  });
  const [betaConfig, setBetaConfig] = useState({
    enabled: false,
    betaEndDate: '',
    gracePeriodDays: 7,
    pricing: [], // Currency-specific pricing
    proFirstMonthPrice: 10, // Legacy
    businessFirstMonthPrice: 25, // Legacy
    yearlyDiscountPercent: 50,
    codePrefix: 'BETA'
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Check if user is Super Admin
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  // Load settings on mount
  useEffect(() => {
    if (isSuperAdmin) {
      loadSettings();
    } else {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [settingsResponse, betaResponse, currenciesResponse] = await Promise.all([
        adminApi.getSystemSettings(),
        adminApi.getBetaConfig(),
        adminApi.getCurrencies()
      ]);
      
      const currencyList = currenciesResponse.data.currencies || [];
      setCurrencies(currencyList);
      
      setSettings({
        registrationEnabled: settingsResponse.data.registrationEnabled,
        bankAccount: settingsResponse.data.bankAccount || {
          bankName: '',
          accountName: '',
          accountNumber: '',
          swiftCode: '',
          branchName: '',
          notes: ''
        }
      });
      
      // Build beta config with currency-specific pricing
      const betaData = betaResponse.data.config;
      const existingPricing = betaData.pricing || [];
      const existingPricingMap = {};
      existingPricing.forEach(p => {
        existingPricingMap[p.currencyCode] = {
          proFirstMonthPrice: p.proFirstMonthPrice,
          businessFirstMonthPrice: p.businessFirstMonthPrice
        };
      });
      
      // Create pricing array for all currencies
      const fullPricing = currencyList.map(currency => ({
        currencyCode: currency.code,
        symbol: currency.symbol,
        isDefault: currency.isDefault,
        proFirstMonthPrice: existingPricingMap[currency.code]?.proFirstMonthPrice ?? (currency.isDefault ? (betaData.proFirstMonthPrice || 10) : ''),
        businessFirstMonthPrice: existingPricingMap[currency.code]?.businessFirstMonthPrice ?? (currency.isDefault ? (betaData.businessFirstMonthPrice || 25) : '')
      }));
      
      // Sort to put default currency first
      fullPricing.sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
      
      setBetaConfig({
        ...betaData,
        pricing: fullPricing
      });
      setLastUpdated(settingsResponse.data.updatedAt);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRegistration = async (enabled) => {
    setSaving(true);
    try {
      const response = await adminApi.updateSystemSettings({
        registrationEnabled: enabled
      });
      
      setSettings(prev => ({
        ...prev,
        registrationEnabled: response.data.registrationEnabled
      }));
      setLastUpdated(response.data.updatedAt);
      
      toast.success(
        enabled 
          ? 'Public registration enabled' 
          : 'Public registration disabled'
      );
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveBankAccount = async (e) => {
    e.preventDefault();
    setSavingBank(true);
    try {
      const response = await adminApi.updateSystemSettings({
        bankAccount: settings.bankAccount
      });
      
      setSettings(prev => ({
        ...prev,
        bankAccount: response.data.bankAccount || prev.bankAccount
      }));
      setLastUpdated(response.data.updatedAt);
      
      toast.success('Bank account information saved');
    } catch (error) {
      console.error('Failed to save bank account:', error);
      toast.error('Failed to save bank account information');
    } finally {
      setSavingBank(false);
    }
  };
  
  const updateBankField = (field, value) => {
    setSettings(prev => ({
      ...prev,
      bankAccount: {
        ...prev.bankAccount,
        [field]: value
      }
    }));
  };
  
  const handleToggleBetaMode = async (newEnabled) => {
    const previousConfig = { ...betaConfig };
    
    // Optimistically update UI
    setBetaConfig(prev => ({ ...prev, enabled: newEnabled }));
    setSavingBeta(true);
    
    try {
      const response = await adminApi.updateBetaConfig({ enabled: newEnabled });
      
      // If the API call succeeded, trust that the update worked
      // Only update other config values from response, keep our enabled state
      if (response.data?.config) {
        setBetaConfig(prev => ({
          ...response.data.config,
          enabled: newEnabled  // Keep the value we set
        }));
      }
      
      toast.success(
        newEnabled 
          ? 'Beta mode enabled - invite codes are now required' 
          : 'Beta mode disabled'
      );
    } catch (error) {
      // Revert on error
      setBetaConfig(previousConfig);
      console.error('Failed to update beta mode:', error);
      toast.error(error.response?.data?.error || 'Failed to update beta mode');
    } finally {
      setSavingBeta(false);
    }
  };
  
  const handleSaveBetaConfig = async (e) => {
    e.preventDefault();
    setSavingBeta(true);
    try {
      // Only include pricing entries that have at least one price set
      const validPricing = betaConfig.pricing
        .filter(p => (p.proFirstMonthPrice !== '' && p.proFirstMonthPrice !== undefined) || 
                     (p.businessFirstMonthPrice !== '' && p.businessFirstMonthPrice !== undefined))
        .map(p => ({
          currencyCode: p.currencyCode,
          proFirstMonthPrice: p.proFirstMonthPrice !== '' ? Number(p.proFirstMonthPrice) : 0,
          businessFirstMonthPrice: p.businessFirstMonthPrice !== '' ? Number(p.businessFirstMonthPrice) : 0
        }));
      
      const configToSave = {
        ...betaConfig,
        pricing: validPricing
      };
      
      const response = await adminApi.updateBetaConfig(configToSave);
      const savedConfig = response.data.config;
      
      // Rebuild the pricing array with all currencies after save
      const existingPricing = savedConfig.pricing || [];
      const existingPricingMap = {};
      existingPricing.forEach(p => {
        existingPricingMap[p.currencyCode] = {
          proFirstMonthPrice: p.proFirstMonthPrice,
          businessFirstMonthPrice: p.businessFirstMonthPrice
        };
      });
      
      // Use same logic as loadSettings - fallback to legacy prices for default currency
      const fullPricing = currencies.map(currency => ({
        currencyCode: currency.code,
        symbol: currency.symbol,
        isDefault: currency.isDefault,
        proFirstMonthPrice: existingPricingMap[currency.code]?.proFirstMonthPrice ?? 
          (currency.isDefault ? (savedConfig.proFirstMonthPrice || 10) : ''),
        businessFirstMonthPrice: existingPricingMap[currency.code]?.businessFirstMonthPrice ?? 
          (currency.isDefault ? (savedConfig.businessFirstMonthPrice || 25) : '')
      }));
      
      fullPricing.sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
      
      setBetaConfig({
        ...savedConfig,
        pricing: fullPricing
      });
      toast.success('Beta configuration saved');
    } catch (error) {
      console.error('Failed to save beta config:', error);
      toast.error('Failed to save beta configuration');
    } finally {
      setSavingBeta(false);
    }
  };
  
  const updateBetaField = (field, value) => {
    setBetaConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const updateBetaPricing = (index, field, value) => {
    const newPricing = [...betaConfig.pricing];
    newPricing[index] = { ...newPricing[index], [field]: value };
    setBetaConfig(prev => ({ ...prev, pricing: newPricing }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  // Access Denied for non-Super Admins
  if (!isSuperAdmin) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-surface-100">System Settings</h1>
            <p className="text-surface-400 mt-1">Configure platform-wide settings</p>
          </div>
        </div>
        
        <div className="card border-red-500/30 bg-red-500/5 py-12">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="p-4 rounded-full bg-red-500/10">
              <LockClosedIcon className="w-12 h-12 text-red-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-surface-100">Access Denied</h3>
              <p className="text-surface-400 mt-2 max-w-md">
                System Settings are only accessible to Super Admins. 
                Please contact your system administrator if you need access.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">System Settings</h1>
          <p className="text-surface-400 mt-1">Configure platform-wide settings</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-surface-500">
          <Cog6ToothIcon className="w-4 h-4" />
          <span>Super Admin Only</span>
        </div>
      </div>

      {/* Registration Settings */}
      <SettingCard
        title="Public Registration"
        description="Allow new companies to register through the public registration page."
        icon={UserPlusIcon}
        infoText="When disabled, new companies can only be created through the admin panel. This is useful during beta testing when you want to invite users manually."
      >
        <div className="flex items-center justify-between p-4 bg-surface-800/50 rounded-lg border border-surface-700">
          <div>
            <p className="font-medium text-surface-200">
              {settings.registrationEnabled ? 'Registration is Open' : 'Registration is Closed'}
            </p>
            <p className="text-sm text-surface-500 mt-0.5">
              {settings.registrationEnabled 
                ? 'New companies can sign up at /register'
                : 'Only admins can create new companies'}
            </p>
          </div>
          <ToggleSwitch
            enabled={settings.registrationEnabled}
            onChange={handleToggleRegistration}
            disabled={saving}
          />
        </div>

        {/* Status indicator */}
        <div className={`mt-3 flex items-center gap-2 ${
          settings.registrationEnabled ? 'text-emerald-400' : 'text-amber-400'
        }`}>
          <div className={`w-2 h-2 rounded-full ${
            settings.registrationEnabled ? 'bg-emerald-400' : 'bg-amber-400'
          }`} />
          <span className="text-sm">
            {settings.registrationEnabled 
              ? 'Public registration is active' 
              : 'Running in invite-only mode'}
          </span>
        </div>
      </SettingCard>
      
      {/* Beta Launch Settings */}
      <SettingCard
        title="Beta Launch Mode"
        description="Configure beta launch settings including invite codes and special pricing for early adopters."
        icon={SparklesIcon}
        infoText="When beta mode is enabled, users must have a valid invite code to register. Beta users get special perks including free trial of Basic plan and discounted upgrade pricing."
      >
        <div className="space-y-6">
          {/* Beta Mode Toggle */}
          <div className="flex items-center justify-between p-4 bg-surface-800/50 rounded-lg border border-surface-700">
            <div>
              <p className="font-medium text-surface-200">
                {betaConfig.enabled ? 'Beta Mode Active' : 'Beta Mode Disabled'}
              </p>
              <p className="text-sm text-surface-500 mt-0.5">
                {betaConfig.enabled 
                  ? 'Invite codes required for registration'
                  : 'Normal registration flow'}
              </p>
            </div>
            <ToggleSwitch
              enabled={betaConfig.enabled}
              onChange={handleToggleBetaMode}
              disabled={savingBeta}
            />
          </div>
          
          {/* Status indicator */}
          <div className={`flex items-center gap-2 ${
            betaConfig.enabled ? 'text-purple-400' : 'text-surface-500'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              betaConfig.enabled ? 'bg-purple-400 animate-pulse' : 'bg-surface-500'
            }`} />
            <span className="text-sm">
              {betaConfig.enabled 
                ? 'Beta launch is active - invite codes required' 
                : 'Beta mode is off'}
            </span>
          </div>
          
          {/* Beta Configuration Form - Only show when beta mode is enabled */}
          {betaConfig.enabled && (
            <>
              <form onSubmit={handleSaveBetaConfig} className="space-y-4 border-t border-surface-700 pt-6">
                <h4 className="text-sm font-medium text-surface-300 flex items-center gap-2">
                  <TicketIcon className="w-4 h-4" />
                  Beta Configuration
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      Beta End Date
                    </label>
                    <input
                      type="date"
                      value={betaConfig.betaEndDate ? new Date(betaConfig.betaEndDate).toISOString().split('T')[0] : ''}
                      onChange={(e) => updateBetaField('betaEndDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                      className="input"
                    />
                    <p className="text-xs text-surface-500 mt-1">Fixed date when beta trial ends for all users</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      Grace Period Days
                    </label>
                    <input
                      type="number"
                      value={betaConfig.gracePeriodDays}
                      onChange={(e) => updateBetaField('gracePeriodDays', parseInt(e.target.value))}
                      className="input"
                      min="1"
                      max="30"
                    />
                    <p className="text-xs text-surface-500 mt-1">Days after beta ends to upgrade</p>
                  </div>
                </div>
                
                {/* Currency-specific Beta Pricing */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-surface-300">
                    First Month Upgrade Prices by Currency
                  </label>
                  <p className="text-xs text-surface-500">
                    Set the special first month prices for Pro and Business plan upgrades in each currency.
                  </p>
                  
                  {betaConfig.pricing?.map((pricing, index) => (
                    <div key={pricing.currencyCode} className="bg-surface-700/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-surface-300">
                            {pricing.currencyCode}
                          </span>
                          {pricing.isDefault && (
                            <span className="px-1.5 py-0.5 text-xs bg-primary-500/20 text-primary-400 rounded">Default</span>
                          )}
                        </div>
                        <span className="text-xs text-surface-500">{pricing.symbol}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-surface-500 mb-1">Pro First Month</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm">
                              {pricing.symbol}
                            </span>
                            <input
                              type="number"
                              value={pricing.proFirstMonthPrice}
                              onChange={(e) => updateBetaPricing(index, 'proFirstMonthPrice', e.target.value)}
                              className="input pl-8 text-sm"
                              min="0"
                              step="0.01"
                              placeholder="Not set"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-surface-500 mb-1">Business First Month</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm">
                              {pricing.symbol}
                            </span>
                            <input
                              type="number"
                              value={pricing.businessFirstMonthPrice}
                              onChange={(e) => updateBetaPricing(index, 'businessFirstMonthPrice', e.target.value)}
                              className="input pl-8 text-sm"
                              min="0"
                              step="0.01"
                              placeholder="Not set"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      Yearly Discount (%)
                    </label>
                    <input
                      type="number"
                      value={betaConfig.yearlyDiscountPercent}
                      onChange={(e) => updateBetaField('yearlyDiscountPercent', parseInt(e.target.value))}
                      className="input"
                      min="0"
                      max="100"
                    />
                    <p className="text-xs text-surface-500 mt-1">Discount for founders after first month</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      Invite Code Prefix
                    </label>
                    <input
                      type="text"
                      value={betaConfig.codePrefix}
                      onChange={(e) => updateBetaField('codePrefix', e.target.value.toUpperCase())}
                      className="input"
                      placeholder="BETA"
                      maxLength="10"
                    />
                  </div>
                </div>
                
                <button 
                  type="submit" 
                  disabled={savingBeta}
                  className="btn-primary w-full"
                >
                  {savingBeta ? 'Saving...' : 'Save Beta Configuration'}
                </button>
              </form>
              
              {/* Beta Perks Summary */}
              <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <h4 className="text-sm font-medium text-purple-300 mb-2">Beta User Perks</h4>
                <ul className="text-sm text-surface-400 space-y-1">
                  <li>• Free Basic plan until {betaConfig.betaEndDate ? new Date(betaConfig.betaEndDate).toLocaleDateString() : 'TBD'}</li>
                  <li>• {betaConfig.gracePeriodDays} days grace period after beta ends to decide</li>
                  <li>• Permanent Founders Badge</li>
                  <li>• Basic: Free during beta, then {betaConfig.yearlyDiscountPercent}% off for a year</li>
                  {betaConfig.pricing?.filter(p => p.proFirstMonthPrice !== '' && p.proFirstMonthPrice !== undefined).map(p => (
                    <li key={`pro-${p.currencyCode}`}>
                      • Pro upgrade ({p.currencyCode}): {p.symbol}{p.proFirstMonthPrice} first month, then {betaConfig.yearlyDiscountPercent}% off for a year
                    </li>
                  ))}
                  {betaConfig.pricing?.filter(p => p.businessFirstMonthPrice !== '' && p.businessFirstMonthPrice !== undefined).map(p => (
                    <li key={`biz-${p.currencyCode}`}>
                      • Business upgrade ({p.currencyCode}): {p.symbol}{p.businessFirstMonthPrice} first month, then {betaConfig.yearlyDiscountPercent}% off for a year
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </SettingCard>
      
      {/* Bank Account Information */}
      <SettingCard
        title="Bank Account Information"
        description="Configure your company bank account for receiving invoice payments via bank transfer."
        icon={BanknotesIcon}
        infoText="This information will be included in invoices sent to Enterprise customers who pay via bank transfer."
      >
        <form onSubmit={handleSaveBankAccount} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Bank Name *
              </label>
              <input
                type="text"
                value={settings.bankAccount.bankName}
                onChange={(e) => updateBankField('bankName', e.target.value)}
                className="input"
                placeholder="e.g., Bank Central Asia (BCA)"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Branch Name
              </label>
              <input
                type="text"
                value={settings.bankAccount.branchName}
                onChange={(e) => updateBankField('branchName', e.target.value)}
                className="input"
                placeholder="e.g., Jakarta Main Branch"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Account Name *
              </label>
              <input
                type="text"
                value={settings.bankAccount.accountName}
                onChange={(e) => updateBankField('accountName', e.target.value)}
                className="input"
                placeholder="e.g., PT Neoshift Indonesia"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Account Number *
              </label>
              <input
                type="text"
                value={settings.bankAccount.accountNumber}
                onChange={(e) => updateBankField('accountNumber', e.target.value)}
                className="input"
                placeholder="e.g., 1234567890"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">
              SWIFT/BIC Code (for international transfers)
            </label>
            <input
              type="text"
              value={settings.bankAccount.swiftCode}
              onChange={(e) => updateBankField('swiftCode', e.target.value)}
              className="input"
              placeholder="e.g., CENAIDJA"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">
              Additional Notes
            </label>
            <textarea
              value={settings.bankAccount.notes}
              onChange={(e) => updateBankField('notes', e.target.value)}
              className="input"
              rows="3"
              placeholder="e.g., Please include invoice number as payment reference"
            />
          </div>
          
          <button 
            type="submit" 
            disabled={savingBank}
            className="btn-primary w-full"
          >
            {savingBank ? 'Saving...' : 'Save Bank Account Information'}
          </button>
        </form>
      </SettingCard>

      {/* Security Notice */}
      <div className="card border-amber-500/30 bg-amber-500/5">
        <div className="flex items-start gap-3">
          <ShieldCheckIcon className="w-6 h-6 text-amber-400 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-surface-100">Security Reminder</h3>
            <p className="text-surface-400 text-sm mt-1">
              When public registration is enabled, anyone can create a company account. 
              All new accounts require email verification before becoming active. 
              Make sure to review new registrations regularly.
            </p>
          </div>
        </div>
      </div>

      {/* Last Updated */}
      {lastUpdated && (
        <p className="text-sm text-surface-500 text-right">
          Last updated: {new Date(lastUpdated).toLocaleString()}
        </p>
      )}
    </div>
  );
}
